import { useMemo } from 'react'

import { t } from 'i18next'
import type { Moment } from 'moment'
import moment from 'moment-timezone'

import { useAppSelector } from '~/store/hooks'

import { Button } from '../Buttons'

import * as S from './styles'

type PresetItem = { text: string; start: Moment; end: Moment }

const DatePresets = (props: {
  showDateFormat?: string
  handlePresets: (start: Moment, end: Moment) => void
  /** When set, chip list and selection highlight use these instead of defaults + Redux */
  presets?: PresetItem[]
  selectionStart?: Moment | null
  selectionEnd?: Moment | null
}) => {
  const {
    showDateFormat = t('common:patterns.date'),
    handlePresets,
    presets: presetsProp,
    selectionStart: selectionStartProp,
    selectionEnd: selectionEndProp
  } = props
  const { startDate: startFromStore, endDate: endFromStore } = useAppSelector(
    state => state.datepickerHeader
  )

  /**
   * Default datepicker: only Redux (unchanged from before).
   * When `presets` is passed (e.g. spend-reports), use optional selection for chip highlight.
   */
  const hasCustomPresets = presetsProp !== undefined
  const startDate = hasCustomPresets
    ? (selectionStartProp ?? startFromStore)
    : startFromStore
  const endDate = hasCustomPresets
    ? (selectionEndProp ?? endFromStore)
    : endFromStore

  const today = moment()

  const defaultPresets = useMemo(
    () =>
      [
        {
          text: t('common:words.today'),
          start: today,
          end: today
        },
        {
          text: t('common:words.yesterday'),
          start: moment().subtract(1, 'day'),
          end: moment().subtract(1, 'day')
        },
        {
          text: t('common:labels.lastXDays', { days: 7 }),
          start: moment().subtract(6, 'day'),
          end: today
        },
        {
          text: t('common:labels.lastXDays', { days: 15 }),
          start: moment().subtract(14, 'day'),
          end: today
        },
        {
          text: t('common:labels.lastXDays', { days: 30 }),
          start: moment().subtract(29, 'day'),
          end: today
        }
      ].reverse(),
    [today]
  )

  const presets = hasCustomPresets
    ? (presetsProp ?? defaultPresets)
    : defaultPresets

  const isChosen = ({ start, end }: PresetItem) =>
    moment(start).format(showDateFormat) ===
      moment(startDate).format(showDateFormat) &&
    moment(end).format(showDateFormat) ===
      moment(endDate).format(showDateFormat)

  return (
    <S.Container>
      {presets.map(({ text, start, end }) => {
        const selected = isChosen({ start, end })

        return (
          <Button
            key={text}
            text={text}
            template={selected ? 'primary' : 'secondary'}
            size="xsmall"
            onClick={() => handlePresets(start, end)}
            customWidth="auto"
          />
        )
      })}
    </S.Container>
  )
}
export default DatePresets
